import React, { useContext, useEffect, useState } from 'react';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import { Add, ArrowBack, ArrowForward, Delete, Forward, Save, Transform } from '@mui/icons-material';
import B2BPaper from '../../b2b-common/B2BPaper';
import { getMeasurementUnits } from '../components/helper';
import { LoginContext } from '../../../App';
import MeasurementUnitSelector from '../components/MeasurementUnitSelector';
import LabelTranslated from '../../b2b-languages/components/LabelTranslated';
import EditText from '../../b2b-common/EditText';
import { handleObjectFieldChange } from '../../b2b-common/helpers';
import { deleteConversion, saveNewConversion, updateConversion } from '../../../services/b2b-indicators-apiService';

import './ConversionModal.css';

const ConversionModal = ({ open, toChangeConversion = null, onUpdate, onClose }) => {

    const { loginData } = useContext(LoginContext);
    const [error, setError] = useState(null);
    const [measurementUnits, setMeasurementUnits] = useState([]);

    const emptyConversion = {
        "from": null,
        "to": null,
        "directConversionFormula": null,
        "reversedConversionFormula": null,
    }
    const [conversion, setConversion] = useState(emptyConversion);

    useEffect(() => {
        if (toChangeConversion) {
            setConversion(toChangeConversion);
        } else {
            setConversion(emptyConversion);
        }
    }, [toChangeConversion]);

    useEffect(() => {
        if (open && (measurementUnits.length === 0) && !toChangeConversion) {
            getMeasurementUnits(loginData, setMeasurementUnits, setError);
        }
    }, [open, measurementUnits, toChangeConversion]);

    // NEW CONVERSION

    const addNewConversion = async () => {
        try {
            const newConversion = await saveNewConversion(conversion, loginData.accessToken);
            console.log('New conversion saved:', newConversion);
            onUpdate();
        } catch (error) {
            console.error('Error saving new conversion:', error);
        }
    }

    const handleSaveNewConversion = async () => {
        //console.log('Save new conversion: ', conversion);
        if (conversion.from && conversion.to && conversion.directConversionFormula && conversion.reversedConversionFormula) {
            await addNewConversion();
        }
    }

    // UPDATE CONVERSION

    const modifyConversion = async () => {
        try {
            const updatedConversion = await updateConversion(conversion, loginData.accessToken);
            console.log('Conversion updated:', updatedConversion);
            onUpdate();
        } catch (error) {
            console.error('Error updating conversion:', error);
        }
    }

    const handleUpdateConversion = () => {
        console.log('Update conversion');
        if (conversion.from && conversion.to && conversion.directConversionFormula && conversion.reversedConversionFormula) {
            modifyConversion();
        }
    }

    // DELETE CONVERSION

    const removeConversion = async () => {
        try {
            await deleteConversion(conversion, loginData.accessToken);
            console.log('Conversion deleted:', conversion);
            onUpdate();
        } catch (error) {
            console.error('Error deleting conversion:', error);
        }
    }

    const handleDeleteConversion = () => {
        console.log('Delete conversion');
        if (conversion.from && conversion.to) {
            removeConversion();
        }
    }

    // BUTTONS

    const buttonsNewConversion = [
        {
            label: 'Save new conversion',
            confirm: true,
            onConfirm: handleSaveNewConversion,
            icon: <Add />
        },
    ];

    const buttonsUpdateConversion = [
        {
            label: 'Update conversion',
            confirm: true,
            onConfirm: handleUpdateConversion,
            icon: <Save />
        },
        {
            label: 'Delete conversion',
            confirm: true,
            onConfirm: handleDeleteConversion,
            icon: <Delete />
        },
    ];

    //console.log('Conversion:', conversion);

    return (
        <B2BPopUpForm
            open={open}
            icon={<Transform />}
            title={toChangeConversion ? 'Update conversion' : 'New conversion'}
            className={'b2b-conversion-modal'}
            onClose={onClose}
            buttons={toChangeConversion ? buttonsUpdateConversion : buttonsNewConversion}
        >
            <div className="conversion-modal-content-from">
                <B2BPaper>
                    {(measurementUnits.length > 0 && !toChangeConversion) && (
                        <MeasurementUnitSelector
                            units={measurementUnits}
                            selectedUnitId={conversion.from ? conversion.from.quantitativeMeasurementUnitId : null}
                            onChange={(unit) => {
                                handleObjectFieldChange('from', unit, setConversion);
                                //console.log('Conversion:', conversion);
                            }}
                            className="conversion-modal-content-from-select"
                        />
                    )}
                    {conversion.from && (
                        <div className="conversion-modal-content-from-details">
                            <LabelTranslated
                                field="Name" labelValue={conversion.from.quantitativeMeasurementUnitName_label}
                                locale={`Name - ${loginData.locale}`} translation={conversion.from.quantitativeMeasurementUnitName} />
                            <LabelTranslated
                                field="Unit" labelValue={conversion.from.quantitativeMeasurementUnit_label}
                                locale={`Unit - ${loginData.locale}`} translation={conversion.from.quantitativeMeasurementUnit} />
                        </div>
                    )}
                </B2BPaper>
            </div>
            <div className="conversion-modal-content-formula">
                <div className="conversion-modal-content-formula-from-to">
                    <B2BPaper>
                        <ArrowForward />
                        <EditText
                            object={conversion}
                            objectField={"directConversionFormula"}
                            setObjectField={setConversion}
                            label="Direct conversion formula"
                        />
                        <ArrowForward />
                    </B2BPaper>
                </div>
                <div className="conversion-modal-content-formula-to-from">
                    <B2BPaper>
                        <ArrowBack />
                        <EditText
                            object={conversion}
                            objectField={"reversedConversionFormula"}
                            setObjectField={setConversion}
                            label="Reversed conversion formula"
                        />
                        <ArrowBack />
                    </B2BPaper>
                </div>
            </div>
            <div className="conversion-modal-content-to">
                <B2BPaper>
                    {(measurementUnits.length > 0 && !toChangeConversion) && (
                        <MeasurementUnitSelector
                            units={measurementUnits}
                            selectedUnitId={conversion.to ? conversion.to.quantitativeMeasurementUnitId : null}
                            onChange={(unit) => {
                                handleObjectFieldChange('to', unit, setConversion);
                                //console.log('Conversion:', conversion);
                            }}
                            className="conversion-modal-content-to-select"
                        />
                    )}
                    {conversion.to && (
                        <div className="conversion-modal-content-to-details">
                            <LabelTranslated
                                field="Name" labelValue={conversion.to.quantitativeMeasurementUnitName_label}
                                locale={`Name - ${loginData.locale}`} translation={conversion.to.quantitativeMeasurementUnitName} />
                            <LabelTranslated
                                field="Unit" labelValue={conversion.to.quantitativeMeasurementUnit_label}
                                locale={`Unit - ${loginData.locale}`} translation={conversion.to.quantitativeMeasurementUnit} />
                        </div>
                    )}
                </B2BPaper>
            </div>
        </B2BPopUpForm>
    );
};

export default ConversionModal;